import { trim } from 'lodash';
import httpClient from '@common/api/httpClient';
import httpNoAuthClient from '@common/api/httpNoAuthClient';
import paramsSerializer from '@common/api/paramsSerializer';
import {
  type GatewayType,
  type PaginatedItemsResponse,
  type PendingDeviceResponse,
  type PendingRetailerAccessesResponse,
  type Retailer,
  type RetailerAccessRequest,
  RetailerAccessRight,
  type RetailersInfoInstallationsQuery,
  type RetailersInfoInstallationsResponse,
  type RetailersInfoQuery,
  type RetailersInfoResponse,
  type UserAccessResponse,
  UserLevel,
} from '@common/types/api';
import { type GUID, type InstallationId } from '@common/types/brandedTypes';
import { createAdditionalUserFromResponse } from '@common/types/models/AdditionalUser';

export async function fetchRetailerAccess(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<Retailer[] | undefined> {
  try {
    const { data } = await httpClient.get<Retailer[]>(
      `/rest/v1/RetailerAccesses/installation/${installationId}`,
      { signal },
    );
    return data;
  } catch {
    return undefined;
  }
}

export async function addRetailerAccess(
  installationId: InstallationId,
  retailerId: string,
  confirmationUrl?: string,
): Promise<void> {
  const request: RetailerAccessRequest = {
    retailerId,
    installationId,
    accessRight: RetailerAccessRight.Write,
    confirmationUrl,
  };
  await httpClient.post('/rest/v1/RetailerAccesses/', request);
}

export async function removeRetailerAccess(
  installationId: InstallationId,
  retailerId: string,
): Promise<void> {
  await httpClient.delete(
    `/rest/v1/RetailerAccesses/${retailerId}/installation/${installationId}`,
  );
}

/** @throws {ApiError} `not-found` when the device has no user who could accept the request. */
export async function requestDeviceAccess(
  installationId: InstallationId,
  retailerId: string,
  macAddress: string,
  confirmationUrl: string,
): Promise<void> {
  await httpClient.post('/rest/v1/PendingRetailerAccesses', {
    installationId,
    retailerId,
    confirmationUrl,
    macAddress,
  });
}

export async function getRetailerAccessRequestById(
  id: string,
): Promise<PendingRetailerAccessesResponse> {
  const { data } = await httpClient.get<PendingRetailerAccessesResponse>(
    `/rest/v1/PendingRetailerAccesses/${id}`,
  );
  return data;
}

export async function getRetailerAccessRequests(
  installationId: InstallationId,
  signal: AbortSignal,
  retailerId?: string,
) {
  try {
    const { data } = await httpClient.get<PendingRetailerAccessesResponse[]>(
      '/rest/v1/PendingRetailerAccesses',
      { params: { installationId, retailerId, limit: 100 }, signal },
    );
    return data;
  } catch {
    return [];
  }
}

export async function getPendingAccessRequests(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<PendingRetailerAccessesResponse[]> {
  try {
    return (await getRetailerAccessRequests(installationId, signal))
      .filter((item) => !item.rejectedUtc && !item.acceptedUtc);
  } catch {
    return [];
  }
}

export async function acceptPendingAccessRequest(
  id: string,
  key?: string,
) {
  if (key) {
    await httpNoAuthClient.post(`/rest/v1/PendingRetailerAccesses/${id}/accept/${key}`);
  } else {
    await httpClient.post(`/rest/v1/PendingRetailerAccesses/${id}/accept`);
  }
}

export async function rejectPendingAccessRequest(
  id: string,
  key?: string,
) {
  if (key) {
    await httpNoAuthClient.post(`/rest/v1/PendingRetailerAccesses/${id}/reject/${key}`);
  } else {
    await httpClient.post(`/rest/v1/PendingRetailerAccesses/${id}/reject`);
  }
}

export async function fetchPendingDevices(
  skip?: number,
  take?: number,
  filter?: string,
  hasDeviceId?: boolean,
) {
  const { data } = await httpClient.get<PaginatedItemsResponse<PendingDeviceResponse>>(
    '/rest/v1/PendingDevices',
    {
      params: {
        skip,
        take,
        filter,
        hasDeviceId,
      },
      paramsSerializer,
    },
  );
  return data;
}

export async function resendPortalKey(
  id: GUID,
  gatewayType: GatewayType,
) {
  await httpClient.post('/rest/v1/PendingDevices/CloudTokenEmail/Resend', { id, gatewayType });
}

function addNewDeviceAccess(
  userId: string,
  userName: string,
  installationId: InstallationId,
  isOwner: boolean,
) {
  return httpClient.post(`/rest/v1/UserAccesses/${userId}`, {
    accessLevel: 'AdvancedUser',
    installationId,
    isOwner,
    email: userName,
  });
}

export function addNewDeviceOwner(
  userId: string,
  userName: string,
  installationId: InstallationId,
) {
  return addNewDeviceAccess(userId, userName, installationId, true);
}

export function addNewDeviceUser(
  userId: string,
  userName: string,
  installationId: InstallationId,
) {
  return addNewDeviceAccess(userId, userName, installationId, false);
}

export function changeDeviceOwner(
  userId: string,
  installationId: InstallationId,
) {
  return httpClient.put(`/rest/v1/UserAccesses/installation/${installationId}/user/${userId}`, {
    accessLevel: UserLevel.AdvancedUser,
    isOwner: true,
  });
}

export function removeDeviceAccess(
  userId: string,
  installationId: InstallationId,
) {
  return httpClient.delete(`/rest/v1/UserAccesses/installation/${installationId}/user/${userId}`);
}

export async function fetchAdditionalUsers(
  installationId: InstallationId,
) {
  const { data } = await httpClient.get<UserAccessResponse[]>(
    `/rest/v1/UserAccesses/installation/${installationId}`,
  );
  return data.map(createAdditionalUserFromResponse);
}

export async function fetchCompanies(
  query: RetailersInfoQuery,
) {
  const { data } = await httpClient
    .post<PaginatedItemsResponse<RetailersInfoResponse>>(
      '/rest/v1/RetailersInfo',
      query,
    );
  return {
    ...data,
    items: data.items.map((item) => ({
      ...item,
      name: trim(item.name),
    })),
  };
}

export async function fetchCompany(
  retailerId: GUID,
): Promise<RetailersInfoResponse> {
  const { data } = await httpClient.get<RetailersInfoResponse>(
    `/rest/v1/Retailers/${retailerId}`,
  );
  return {
    ...data,
    name: trim(data.name),
  };
}

export async function fetchCompaniesInstallations(
  query: RetailersInfoInstallationsQuery,
): Promise<PaginatedItemsResponse<RetailersInfoInstallationsResponse>> {
  const { data } = await httpClient
    .post<PaginatedItemsResponse<RetailersInfoInstallationsResponse>>(
      '/rest/v1/RetailersInfo/installations',
      query,
    );
  return {
    ...data,
    items: data.items.map((item) => ({
      ...item,
      name: trim(item.name),
    })),
  };
}
